import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import api from '../lib/api';
import { XCircle, AlertTriangle, Package, Clock, Cog } from 'lucide-react';
import { HourMeterScanner } from '../components/HourMeterScanner';

export default function CloseActiveCyclePage() {
  const { user } = useAuth();
  const [machines, setMachines] = useState([]);
  const [selectedMachine, setSelectedMachine] = useState('');
  const [machineCtx, setMachineCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [closureDate, setClosureDate] = useState(new Date().toISOString().split('T')[0]);
  const [closureHours, setClosureHours] = useState('');
  const [closurePhoto, setClosurePhoto] = useState('');
  const [closureOcr, setClosureOcr] = useState(null);
  const [closureReason, setClosureReason] = useState('worn_out');
  const [closureRemarks, setClosureRemarks] = useState('');

  const closureReasons = [
    { value: 'worn_out', label: 'Worn Out' },
    { value: 'broken', label: 'Broken' },
    { value: 'damaged', label: 'Damaged' },
    { value: 'trial_ended', label: 'Trial Ended' },
    { value: 'preventive_replacement', label: 'Preventive Replacement' },
    { value: 'unknown', label: 'Unknown' },
  ];

  useEffect(() => {
    api.get('/machines').then(r => setMachines(r.data.filter(m => m.status === 'active'))).catch(() => {});
  }, []);

  const handleMachineSelect = async (machineId) => {
    setSelectedMachine(machineId);
    setMachineCtx(null);
    if (!machineId) return;
    try {
      setLoading(true);
      const res = await api.get(`/machines/${machineId}/context`);
      setMachineCtx(res.data);
    } catch {
      toast.error('Failed to load machine context');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedMachine) return toast.error('Select a machine');
    if (!machineCtx?.active_cycle) return toast.error('No active cycle to close');
    if (!closureDate) return toast.error('Closure date is required');
    if (!closureHours || parseFloat(closureHours) < 0) return toast.error('Valid closure hours required');
    if (parseFloat(closureHours) < machineCtx.active_cycle.installation_hours) {
      return toast.error(`Closure hours must be >= ${machineCtx.active_cycle.installation_hours}`);
    }
    try {
      setSubmitting(true);
      const res = await api.post('/cycles/close', {
        machine_id: selectedMachine,
        closure_date: closureDate,
        closure_hours: parseFloat(closureHours),
        closure_reason: closureReason,
        closure_remarks: closureRemarks,
        closure_meter_photo: closurePhoto || '',
        closure_ocr_value: closureOcr,
      });
      toast.success(res.data.message);
      setSelectedMachine('');
      setMachineCtx(null);
      setClosureHours('');
      setClosurePhoto('');
      setClosureOcr(null);
      setClosureRemarks('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close cycle');
    } finally {
      setSubmitting(false);
    }
  };

  const activeCycle = machineCtx?.active_cycle;
  const machineInfo = machineCtx?.machine;

  return (
    <div className="space-y-6" data-testid="close-cycle-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 font-heading" data-testid="page-title">Close Active Cycle</h1>
        <p className="text-sm text-slate-500 mt-1">Exception workflow: Close an active cycle without installing new tooth points. Use for removed sets, decommissioned machines, or trial ends.</p>
      </div>

      {/* Step 1: Machine Selection */}
      <Card className="p-5">
        <Label className="text-sm font-semibold text-slate-700">Select Machine</Label>
        <Select value={selectedMachine} onValueChange={handleMachineSelect}>
          <SelectTrigger className="mt-2 bg-white" data-testid="machine-select">
            <SelectValue placeholder="Select machine by serial number..." />
          </SelectTrigger>
          <SelectContent>
            {machines.map(m => (
              <SelectItem key={m.machine_id} value={m.machine_id}>
                {m.serial_number} — {m.brand} {m.model} ({m.machine_class})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Card>

      {loading && <div className="text-center py-8 text-slate-400">Loading machine context...</div>}

      {/* Machine Info */}
      {machineInfo && (
        <Card className="p-5 bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-200"><Cog size={18} className="text-slate-600" /></div>
            <div>
              <p className="text-sm font-bold text-slate-900">{machineInfo.brand} {machineInfo.model}</p>
              <p className="text-xs text-slate-500">S/N: {machineInfo.serial_number} | {machineCtx.site?.name}</p>
            </div>
          </div>
        </Card>
      )}

      {/* No Active Cycle */}
      {machineInfo && !activeCycle && (
        <Card className="p-5 border-slate-200 bg-slate-50 text-center">
          <XCircle size={24} className="mx-auto text-slate-400 mb-2" />
          <p className="text-sm text-slate-500">No active cycle found for this machine.</p>
          <p className="text-xs text-slate-400 mt-1">Use "New Installation / Change-Out" to create one.</p>
        </Card>
      )}

      {/* Active Cycle Details + Closure Form */}
      {activeCycle && (
        <Card className="p-5 border-amber-200 bg-amber-50/30" data-testid="active-cycle-detail">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle size={20} className="text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">Active Cycle</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Started: {activeCycle.installation_date} | Install Hours: {activeCycle.installation_hours?.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="mb-4 space-y-1">
            {activeCycle.entries?.map((e, i) => (
              <div key={i} className="flex items-center gap-2 text-xs text-slate-600 bg-white p-2 rounded">
                <Package size={12} className="text-slate-400" />
                <span className="font-medium">{e.product_name || e.product_id}</span>
                <span>x{e.quantity}</span>
                {e.supplier_name && <span className="text-slate-400">({e.supplier_name})</span>}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Closure Date *</Label>
              <Input type="date" value={closureDate} onChange={e => setClosureDate(e.target.value)} className="mt-1 bg-white" data-testid="closure-date" />
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label className="text-xs text-slate-600">Closure Hour Meter *</Label>
                <HourMeterScanner onDetected={(v, img) => { setClosureHours(String(v)); setClosurePhoto(img); setClosureOcr(v); }} testId="scan-closure-hours" />
              </div>
              <Input type="number" value={closureHours} onChange={e => setClosureHours(e.target.value)} placeholder={`>= ${activeCycle.installation_hours}`} className="mt-1 bg-white" data-testid="closure-hours" />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Closure Reason</Label>
              <Select value={closureReason} onValueChange={setClosureReason}>
                <SelectTrigger className="mt-1 bg-white" data-testid="closure-reason"><SelectValue /></SelectTrigger>
                <SelectContent>{closureReasons.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Remarks</Label>
              <Textarea value={closureRemarks} onChange={e => setClosureRemarks(e.target.value)} placeholder="Optional closure notes" className="mt-1 bg-white" rows={2} />
            </div>
          </div>
        </Card>
      )}

      {activeCycle && (
        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => { setSelectedMachine(''); setMachineCtx(null); }} data-testid="cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} className="bg-red-600 hover:bg-red-700 text-white" data-testid="close-cycle-btn">
            {submitting ? 'Closing...' : 'Close Active Cycle'}
          </Button>
        </div>
      )}
    </div>
  );
}
